import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, gbp, fmtDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, FileText, Receipt, Users, TrendingUp, Plus } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Overview</div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight font-semibold text-slate-900 mt-1">
            D4PS Logistics — Dashboard
          </h1>
          <p className="text-slate-600 mt-2 text-sm">
            Snapshot of your purchase orders and self-bills.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/purchase-orders/new">
            <Button data-testid="dash-new-po-btn" className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white">
              <Plus className="h-4 w-4 mr-2" /> New Purchase Order
            </Button>
          </Link>
          <Link to="/self-bills/new">
            <Button data-testid="dash-new-sb-btn" variant="outline" className="rounded-sm border-slate-300">
              <Plus className="h-4 w-4 mr-2" /> New Self-Bill
            </Button>
          </Link>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard
          icon={FileText}
          label="Purchase Orders"
          value={loading ? "—" : stats?.purchase_orders_count}
          hint={loading ? "" : `${gbp(stats?.po_total_month || 0)} this month`}
          testid="stat-po"
        />
        <StatCard
          icon={Receipt}
          label="Self-Bills"
          value={loading ? "—" : stats?.self_bills_count}
          hint={loading ? "" : `${gbp(stats?.sb_total_month || 0)} this month`}
          testid="stat-sb"
        />
        <StatCard
          icon={Users}
          label="Contacts"
          value={loading ? "—" : stats?.contacts_count}
          hint="Subcontractors + Drivers"
          testid="stat-contacts"
        />
        <StatCard
          icon={TrendingUp}
          label="Combined (mo.)"
          value={loading ? "—" : gbp((stats?.po_total_month || 0) + (stats?.sb_total_month || 0))}
          hint="PO + Self-Bill totals"
          testid="stat-combined"
        />
      </div>

      {/* Chart */}
      <Card className="rounded-sm border border-slate-200 shadow-none p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Last 6 months</div>
            <h2 className="font-display text-xl font-medium text-slate-900 mt-1">Monthly Totals (£)</h2>
          </div>
        </div>
        <div className="h-72" data-testid="dash-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={stats?.monthly_series || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="month" tick={{ fill: "#64748b", fontSize: 12 }} axisLine={{ stroke: "#cbd5e1" }} />
              <YAxis tick={{ fill: "#64748b", fontSize: 12 }} axisLine={{ stroke: "#cbd5e1" }} />
              <Tooltip
                contentStyle={{ borderRadius: 4, border: "1px solid #e2e8f0", fontSize: 13 }}
                formatter={(v) => gbp(v)}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="po_total" name="Purchase Orders" fill="#0f172a" radius={[2, 2, 0, 0]} />
              <Bar dataKey="sb_total" name="Self-Bills" fill="#475569" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Recent lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RecentList
          title="Recent Purchase Orders"
          to="/purchase-orders"
          items={stats?.recent_pos || []}
          subKey={(it) => it.customer_snapshot?.name}
          testid="recent-pos"
          baseRoute="/purchase-orders"
        />
        <RecentList
          title="Recent Self-Bills"
          to="/self-bills"
          items={stats?.recent_sbs || []}
          subKey={(it) => it.driver_snapshot?.name}
          testid="recent-sbs"
          baseRoute="/self-bills"
        />
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, hint, testid }) {
  return (
    <Card data-testid={testid} className="rounded-sm border border-slate-200 shadow-none p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">{label}</div>
          <div className="mt-3 font-display text-3xl font-semibold text-slate-900">{value}</div>
          {hint && <div className="mt-2 text-xs text-slate-500">{hint}</div>}
        </div>
        <div className="h-9 w-9 rounded-sm bg-slate-900 text-white flex items-center justify-center">
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </Card>
  );
}

function RecentList({ title, to, items, subKey, testid, baseRoute }) {
  return (
    <Card data-testid={testid} className="rounded-sm border border-slate-200 shadow-none">
      <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
        <h3 className="font-display text-base font-medium text-slate-900">{title}</h3>
        <Link to={to} className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
          View all <ArrowUpRight className="h-3 w-3" />
        </Link>
      </div>
      <div className="divide-y divide-slate-100">
        {items.length === 0 && (
          <div className="px-6 py-8 text-sm text-slate-500 text-center">No records yet.</div>
        )}
        {items.map((it) => (
          <Link
            key={it.id}
            to={`${baseRoute}/${it.id}`}
            className="flex items-center justify-between px-6 py-3 hover:bg-slate-50 transition-colors"
            data-testid={`${testid}-row-${it.id}`}
          >
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-900">{it.number}</div>
              <div className="text-xs text-slate-500 truncate">{subKey(it) || "—"} · {fmtDate(it.created_at)}</div>
            </div>
            <div className="text-sm font-medium tabular-nums text-slate-900">{gbp(it.total)}</div>
          </Link>
        ))}
      </div>
    </Card>
  );
}
